// Project form modal — creates (when project=null) or edits an existing project.
import { useState, useEffect } from 'react';
import { useData } from './DataContext.jsx';
import { fmtEuro } from './lib/format.js';
import { descriptionBonusPoints, POINTS_DESCRIPTION_CAP } from './lib/team-ranking.js';
import { stripMentions } from './lib/mentions.js';
import { capitalizeName } from './lib/format.js';
import { Modal, Avatar } from './components.jsx';
import { MentionTextarea } from './mention-input.jsx';
import { TaskAssignee, TaskDueDate } from './projects.jsx';
import { IconX, IconCheck } from './icons.jsx';

const EMPTY_TASK = { title: '', assignee_id: null, due_date: null };

const COLOR_PRESETS = ['#FF6B4A', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#0EA5E9', '#B45309', '#8B5CF6'];

function ProjectFormModal({ project, defaultCustomerId, onClose, onSaved, onConfetti }) {
  const {
    customers, profiles, currentProfile, profileById,
    membersByProject,
    addProject, updateProject, changeProjectDeadline, deleteProject, assignProjectMembers, addTasksBatch,
    syncDriveFolders, syncMentions,
  } = useData();

  const isEdit = !!project;
  const initialDeadline = project?.deadline || '';

  // Derive initial form state — for edit mode, pick "zeit" mode as default (could infer from euro>0 && min==0 but both are usually set)
  const initialHours = project?.budget_minutes ? Math.round(project.budget_minutes / 60) : '';
  const initialEuro = project?.budget_euro ?? '';
  const initialMembers = project ? (membersByProject[project.id] || []) : [];

  const [mode, setMode] = useState('zeit');
  const [title, setTitle] = useState(project?.title || '');
  const [desc, setDesc] = useState(project?.description || '');
  const [deadline, setDeadline] = useState(initialDeadline);
  const [deadlineReason, setDeadlineReason] = useState('');
  const [hours, setHours] = useState(String(initialHours || ''));
  const [euro, setEuro] = useState(String(initialEuro || ''));
  const [isEstimate, setIsEstimate] = useState(project?.budget_is_estimate || false);
  const [rate, setRate] = useState(currentProfile?.hourly_rate || 120);
  const [customerId, setCustomerId] = useState(project?.customer_id || defaultCustomerId || '');
  const [color, setColor] = useState(project?.color || '');
  const [team, setTeam] = useState(initialMembers);
  const [leadId, setLeadId] = useState(project?.lead_id || '');
  const [tags, setTags] = useState(project?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [taskList, setTaskList] = useState([EMPTY_TASK]); // create-mode only — { title, assignee_id, due_date }
  const [celebrated, setCelebrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const hoursNum = parseFloat(hours) || 0;
  const euroNum = parseFloat(euro) || 0;
  const liveEuro = mode === 'zeit' ? hoursNum * rate : euroNum;
  const liveHours = mode === 'zeit' ? hoursNum : (rate ? euroNum / rate : 0);

  // @-Mention-Tokens zählen nicht als „echter" Beschreibungstext.
  const descLen = stripMentions(desc).trim().length;

  const checks = [
    { ok: title.length >= 3, label: 'Projekttitel', xp: 'Pflicht' },
    { ok: descLen >= 20, label: 'Beschreibung (min. 20 Zeichen)', xp: 'Pflicht' },
    { ok: !!deadline, label: 'Deadline', xp: 'Pflicht' },
    { ok: (mode === 'zeit' && hoursNum > 0) || (mode === 'geld' && euroNum > 0), label: 'Budget', xp: 'Pflicht' },
    { ok: !!customerId, label: 'Kunde zugeordnet', xp: '+10 XP' },
    { ok: !!color, label: 'Projektfarbe gewählt', xp: '+5 XP' },
    { ok: team.length > 0, label: 'Team zugewiesen', xp: '+10 XP' },
    { ok: !!leadId, label: 'Projektlead benannt', xp: '+5 XP' },
    { ok: tags.length > 0, label: 'Mindestens ein Tag', xp: '+5 XP' },
  ];
  const filled = checks.filter(c => c.ok).length;
  const pct = Math.round(filled / checks.length * 100);
  const deadlineChanged = isEdit && (deadline || '') !== (initialDeadline || '');
  const reasonOk = !deadlineChanged || deadlineReason.trim().length >= 3;
  const canSave = checks.slice(0, 4).every(c => c.ok) && reasonOk;

  useEffect(() => {
    if (!isEdit && pct === 100 && !celebrated) {
      setCelebrated(true);
      onConfetti && onConfetti();
    }
  }, [pct, celebrated, isEdit, onConfetti]);

  const pctLabel = pct < 40 ? "Ausfüllen lohnt sich" : pct < 75 ? "Gut unterwegs" : pct < 100 ? "Fast fertig" : "Vollständig geplant ⭐";
  const pctColor = pct < 40 ? 'var(--ink-3)' : pct < 75 ? 'var(--warn)' : pct < 100 ? 'var(--accent)' : 'var(--ok)';

  function addTag() {
    if (!tagInput.trim()) return;
    setTags([...tags, tagInput.trim()]);
    setTagInput('');
  }

  async function save() {
    if (!canSave || saving) return;
    setSaving(true); setSaveError(null);
    try {
      const budget_minutes = mode === 'zeit' ? Math.round(hoursNum * 60) : Math.round((euroNum / rate) * 60);
      const budget_euro = mode === 'zeit' ? Math.round(hoursNum * rate) : Math.round(euroNum);
      const payload = {
        customer_id: customerId || null,
        title,
        description: desc,
        deadline: deadline || null,
        budget_minutes,
        budget_euro,
        budget_is_estimate: isEstimate,
        color: color || '#FF6B4A',
        tags,
        lead_id: leadId || null,
      };
      let projectId;
      if (isEdit) {
        // If the deadline shifted, route that change through the audited RPC
        // so the reason is recorded. The remaining fields go via a regular
        // update with deadline excluded (the RPC already moved it).
        if (deadlineChanged) {
          await changeProjectDeadline(project.id, deadline || null, deadlineReason.trim());
          const rest = { ...payload };
          delete rest.deadline;
          await updateProject(project.id, rest);
        } else {
          await updateProject(project.id, payload);
        }
        projectId = project.id;
      } else {
        const created = await addProject({ ...payload, status: 'active' });
        projectId = created.id;
        // Drive-Ordner im Hintergrund anlegen — darf das Anlegen des Projekts
        // nie blockieren oder scheitern lassen. Fehler landen in drive_sync_error
        // und können später über den Button im Projekt erneut versucht werden.
        syncDriveFolders(projectId).catch(() => {});
      }
      // Notify anyone newly @-mentioned in the description.
      await syncMentions({
        text: desc,
        prevText: project?.description || '',
        sourceType: 'project_description',
        sourceId: projectId,
        projectId,
      });
      // Diff & sync team membership
      await assignProjectMembers(projectId, team);
      // Insert tasks (create mode only — edit mode manages tasks on the detail page)
      if (!isEdit) {
        const items = taskList.filter(t => t.title.trim());
        if (items.length > 0) await addTasksBatch(projectId, items);
      }
      onSaved?.(projectId);
      onClose();
    } catch (e) {
      setSaveError(e.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    const ok = window.confirm(
      `Projekt „${project.title}" wirklich löschen? Alle zugehörigen Zeiteinträge werden ebenfalls gelöscht. Das lässt sich nicht rückgängig machen.`
    );
    if (!ok) return;
    setDeleting(true); setSaveError(null);
    try {
      await deleteProject(project.id);
      onSaved?.();
      onClose();
    } catch (e) {
      setSaveError(e.message || 'Fehler beim Löschen');
      setDeleting(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="eyebrow">{isEdit ? 'Projekt bearbeiten' : 'Neues Projekt'}</div>
            <h2 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: '4px 0 0', letterSpacing: '-0.01em' }}>
              {title || (isEdit ? '—' : "Wie heißt dein neues Projekt?")}
            </h2>
          </div>
          <button className="icon-btn" onClick={onClose}><IconX size={14}/></button>
        </div>
        {!isEdit && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: pctColor }}>Projekt-Qualität · {pctLabel}</div>
              <div className="mono tiny" style={{ color: pctColor, fontWeight: 600 }}>{pct}%</div>
            </div>
            <div className="bar thick">
              <span style={{ width: `${pct}%`, background: pct < 40 ? 'var(--ink-3)' : pct < 75 ? 'var(--warn)' : pct < 100 ? 'var(--accent)' : 'var(--ok)' }}/>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
        <div className="grid grid-2" style={{ gap: 16 }}>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Projekttitel *</label>
            <input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Rebranding Möbelwerk Müller"/>
          </div>

          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>
              Beschreibung *
              <span className="tiny muted" style={{ marginLeft: 8, fontWeight: 500 }}>
                {descLen < 20 ? `noch ${20 - descLen} Zeichen` : '✓ ausreichend'}
              </span>
            </label>
            <MentionTextarea value={desc} onChange={setDesc} profiles={profiles} placeholder="Was ist der Scope? Welche Deliverables? Welche Besonderheiten? Tippe @ um jemanden zu erwähnen."/>
            {!isEdit && (() => {
              const pts = descriptionBonusPoints(desc);
              const remaining = Math.max(0, 50 - descLen);
              if (descLen === 0) return null;
              return (
                <div className="tiny" style={{
                  marginTop: 6,
                  color: pts > 0 ? 'var(--ok)' : 'var(--ink-3)',
                }}>
                  {pts > 0
                    ? `+${pts} Bonus-Punkte für ausführliches Einpflegen${pts < POINTS_DESCRIPTION_CAP ? ` (Max ${POINTS_DESCRIPTION_CAP} bei 500 Zeichen)` : ' · Maximum erreicht 🏆'}`
                    : `Noch ${remaining} Zeichen bis zum ersten Bonus-Punkt für ausführliches Einpflegen`
                  }
                </div>
              );
            })()}
          </div>

          <div className="field">
            <label>Deadline *</label>
            <input type="date" className="input" value={deadline} onChange={e => setDeadline(e.target.value)}/>
            {isEdit && initialDeadline && (
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Ursprüngliche Deadline: <b style={{ color: 'var(--ink-2)' }}>{new Date(project.original_deadline || initialDeadline).toLocaleDateString('de-DE')}</b>
              </div>
            )}
          </div>

          {deadlineChanged && (
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label style={{ color: 'var(--warn)' }}>
                Grund für Deadline-Verschiebung *
                <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>
                  Pflichtfeld — wird für alle dauerhaft dokumentiert
                </span>
              </label>
              <textarea
                className="textarea"
                value={deadlineReason}
                onChange={e => setDeadlineReason(e.target.value)}
                placeholder="Warum verschiebt sich die Deadline? (z.B. Kunde hat Anforderungen erweitert, Lieferengpass beim Druckpartner …)"
                style={{ minHeight: 70 }}
              />
              {!reasonOk && deadlineReason.length > 0 && (
                <div className="tiny" style={{ color: 'var(--warn)', marginTop: 4 }}>
                  Mindestens 3 Zeichen.
                </div>
              )}
            </div>
          )}

          <div className="field">
            <label>Kunde {!isEdit && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>+10 XP</span>}</label>
            <select className="select" value={customerId} onChange={e => setCustomerId(e.target.value)}>
              <option value="">Kunde wählen…</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Budget * {isEstimate && <span className="tiny" style={{ marginLeft: 6, fontWeight: 600, color: 'var(--warn)' }}>· geschätzt</span>}</label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', padding: 10, background: 'var(--bg-sunken)', borderRadius: 12, border: '1px solid var(--stroke-2)' }}>
              <div className="seg" style={{ alignSelf: 'flex-start' }}>
                <button className={mode === 'zeit' ? 'on' : ''} onClick={() => setMode('zeit')}>⏱ Zeit</button>
                <button className={mode === 'geld' ? 'on' : ''} onClick={() => setMode('geld')}>€ Geld</button>
              </div>
              <div style={{ flex: 1 }}>
                {mode === 'zeit' ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input className="input" style={{ background: 'var(--bg-card)' }} type="number" min="0" placeholder="Stunden" value={hours} onChange={e => setHours(e.target.value)}/>
                    <span className="muted" style={{ fontSize: 12 }}>Stunden</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input className="input" style={{ background: 'var(--bg-card)' }} type="number" min="0" placeholder="Betrag" value={euro} onChange={e => setEuro(e.target.value)}/>
                    <span className="muted" style={{ fontSize: 12 }}>€</span>
                  </div>
                )}
                <div className="tiny muted" style={{ marginTop: 8 }}>
                  {mode === 'zeit'
                    ? <>≈ <span className="mono" style={{ color: 'var(--ink)' }}>{fmtEuro(liveEuro)}</span> bei {rate}€/h</>
                    : <>≈ <span className="mono" style={{ color: 'var(--ink)' }}>{liveHours.toFixed(1).replace('.', ',')} Std.</span> bei {rate}€/h</>
                  }
                </div>
              </div>
              <div style={{ width: 130 }}>
                <div className="tiny muted" style={{ marginBottom: 4 }}>Stundensatz</div>
                <input className="input" style={{ background: 'var(--bg-card)' }} type="number" value={rate} onChange={e => setRate(parseFloat(e.target.value) || 0)}/>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, cursor: 'pointer', fontWeight: 400 }}>
              <input type="checkbox" style={{ marginTop: 2 }} checked={isEstimate} onChange={e => setIsEstimate(e.target.checked)}/>
              <span className="tiny muted">
                Schätzung – noch kein fixes Budget vereinbart.
                Der Wert dient als geschätztes Budget und wird überall als „geschätzt" gekennzeichnet.
              </span>
            </label>
          </div>

          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Projektfarbe {!isEdit && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>+5 XP</span>}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {COLOR_PRESETS.map(c => (
                <button key={c} onClick={() => setColor(c)} style={{
                  width: 32, height: 32, borderRadius: 8, border: color === c ? '2px solid var(--ink)' : '2px solid transparent',
                  background: c, cursor: 'pointer', padding: 0, outline: 0,
                }}/>
              ))}
            </div>
          </div>

          <div className="field">
            <label>Team {!isEdit && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>+10 XP</span>}</label>
            {profiles.length === 0 ? (
              <div className="tiny muted">Noch keine Team-Mitglieder eingeladen.</div>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {profiles.map(u => {
                  const on = team.includes(u.id);
                  return (
                    <button key={u.id} onClick={() => {
                      setTeam(on ? team.filter(x => x !== u.id) : [...team, u.id]);
                      if (on && leadId === u.id) setLeadId(''); // removed member can't stay lead
                      // Aufgaben, die dem entfernten Mitglied zugewiesen waren, wieder freigeben.
                      if (on) setTaskList(ts => ts.map(t => t.assignee_id === u.id ? { ...t, assignee_id: null } : t));
                    }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 4px',
                                     background: on ? 'var(--accent-soft)' : 'var(--bg-sunken)',
                                     border: on ? '1px solid var(--accent-soft-2)' : '1px solid var(--stroke)',
                                     borderRadius: 999, cursor: 'pointer', fontSize: 12, color: on ? 'var(--accent)' : 'var(--ink-2)', fontFamily: 'inherit' }}>
                      <Avatar user={u} size={20}/>
                      {capitalizeName((u.full_name || u.email).split(' ')[0])}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="field">
            <label>Projektlead {!isEdit && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>+5 XP</span>}</label>
            {team.length === 0 ? (
              <div className="tiny muted">Erst Team zuweisen, dann Lead wählen.</div>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {profiles.filter(u => team.includes(u.id)).map(u => {
                  const on = leadId === u.id;
                  return (
                    <button key={u.id} onClick={() => setLeadId(on ? '' : u.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 4px',
                                     background: on ? 'var(--accent-soft)' : 'var(--bg-sunken)',
                                     border: on ? '1px solid var(--accent-soft-2)' : '1px solid var(--stroke)',
                                     borderRadius: 999, cursor: 'pointer', fontSize: 12, color: on ? 'var(--accent)' : 'var(--ink-2)', fontFamily: 'inherit' }}>
                      <Avatar user={u} size={20} badge={on ? { emoji: '⭐', title: 'Projektlead' } : undefined}/>
                      {capitalizeName((u.full_name || u.email).split(' ')[0])}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="field">
            <label>Tags {!isEdit && <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>+5 XP</span>}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input className="input" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="z.B. Rebranding"/>
              <button className="btn" onClick={addTag}>+</button>
            </div>
            {tags.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {tags.map(t => <span key={t} className="chip">{t} <span style={{ cursor: 'pointer', marginLeft: 2 }} onClick={() => setTags(tags.filter(x => x !== t))}>×</span></span>)}
              </div>
            )}
          </div>

          {!isEdit && (
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>
                Aufgaben <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>+3 P pro fertiger Aufgabe im Wettbewerb</span>
              </label>
              <div className="tiny muted" style={{ marginBottom: 8, lineHeight: 1.5 }}>
                Was gehört alles zu diesem Projekt? Splitte es in einzelne Aufgaben — die Liste lässt sich später jederzeit erweitern. Enter springt zur nächsten Zeile. Optional: Aufgabe einem Teammitglied zuweisen und eine eigene Deadline setzen (ohne Datum gilt die Projekt-Deadline).
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {taskList.map((task, i) => {
                  const title = task.title;
                  const patchTask = patch => setTaskList(ts => ts.map((t, j) => j === i ? { ...t, ...patch } : t));
                  return (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="mono tiny muted" style={{ width: 22 }}>{i + 1}.</span>
                    <input
                      className="input"
                      value={title}
                      placeholder="z.B. Logo entwerfen"
                      onChange={e => patchTask({ title: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (i === taskList.length - 1 && title.trim()) {
                            setTaskList([...taskList, EMPTY_TASK]);
                          }
                        } else if (e.key === 'Backspace' && !title && taskList.length > 1) {
                          e.preventDefault();
                          const next = taskList.slice();
                          next.splice(i, 1);
                          setTaskList(next.length ? next : [EMPTY_TASK]);
                        }
                      }}
                    />
                    <TaskDueDate
                      task={{ due_date: task.due_date, done: false }}
                      project={{ deadline }}
                      canEdit
                      onChange={due => patchTask({ due_date: due })}
                    />
                    <TaskAssignee
                      assigneeId={task.assignee_id}
                      members={team}
                      profileById={profileById}
                      canEdit
                      onAssign={uid => patchTask({ assignee_id: uid })}
                    />
                    {taskList.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: '4px 8px', color: 'var(--ink-3)' }}
                        onClick={() => {
                          const next = taskList.slice();
                          next.splice(i, 1);
                          setTaskList(next.length ? next : [EMPTY_TASK]);
                        }}
                      >×</button>
                    )}
                  </div>
                  );
                })}
                <button type="button" className="btn btn-ghost" style={{ alignSelf: 'flex-start', padding: '4px 10px' }}
                        onClick={() => setTaskList([...taskList, EMPTY_TASK])}>
                  + Aufgabe hinzufügen
                </button>
              </div>
            </div>
          )}
        </div>

        {!isEdit && (
          <div style={{ marginTop: 20, padding: 14, background: 'var(--bg-sunken)', borderRadius: 12, border: '1px solid var(--stroke)' }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Qualitäts-Checkliste</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {checks.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.ok ? 'var(--ink)' : 'var(--ink-3)' }}>
                  <span style={{
                    width: 16, height: 16, borderRadius: 4,
                    background: c.ok ? 'var(--ok)' : 'transparent',
                    border: c.ok ? '0' : '1.5px solid var(--stroke-2)',
                    display: 'grid', placeItems: 'center', color: '#fff', flex: '0 0 16px',
                    transition: 'all 200ms',
                  }}>{c.ok && <IconCheck size={10} stroke={3}/>}</span>
                  <span style={{ flex: 1 }}>{c.label}</span>
                  <span className="tiny muted">{c.xp}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {saveError && (
          <div className="tiny" style={{ color: 'var(--crit)', marginTop: 12 }}>{saveError}</div>
        )}
      </div>

      <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        {isEdit ? (
          <button className="btn" onClick={handleDelete} disabled={saving || deleting}
                  style={{ color: 'var(--crit)', borderColor: 'rgba(239,68,68,0.3)' }}>
            {deleting ? 'Lösche…' : 'Projekt löschen'}
          </button>
        ) : (
          <div className="tiny muted">
            {canSave ? "Alle Pflichtfelder erfüllt — du kannst speichern." : "Pflichtfelder ergänzen, dann geht's los."}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving || deleting}>Abbrechen</button>
          <button className="btn btn-primary" disabled={!canSave || saving || deleting}
                  style={{ opacity: (canSave && !saving && !deleting) ? 1 : 0.45, cursor: (canSave && !saving && !deleting) ? 'pointer' : 'not-allowed' }}
                  onClick={save}>
            {saving ? 'Speichere…' : isEdit ? 'Änderungen speichern' : 'Projekt anlegen'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export { ProjectFormModal };
